import React, { useState, memo, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
  Platform,
} from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { COMMUNITY_RESOURCE_ICONS, CommunityResourceIconKey } from '@/assets/admin/CommunityResourceIcons';
import { resioIconOptionMappings } from '@/types/admin/resources';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';

interface IconSelectorProps {
  selectedIcon: string;
  onIconSelect: (iconKey: string) => void;
  error?: string;
}

export const IconSelector = memo<IconSelectorProps>(({
  selectedIcon,
  onIconSelect,
  error,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<AnchorRect | null>(null);
  const anchorRef = useRef<any>(null);

  // Get the selected icon component
  const SelectedIconComponent = COMMUNITY_RESOURCE_ICONS[selectedIcon as CommunityResourceIconKey] || COMMUNITY_RESOURCE_ICONS.photoIcon;
  const selectedIconLabel = resioIconOptionMappings[selectedIcon as keyof typeof resioIconOptionMappings] || 'Photo';

  const handleIconSelect = (iconKey: string) => {
    onIconSelect(iconKey);
    setShowModal(false);
    setDropdownOpen(false);
  };

  const renderIconItem = (iconKey: string, iconLabel: string) => {
    const IconComponent = COMMUNITY_RESOURCE_ICONS[iconKey as CommunityResourceIconKey];
    const isSelected = iconKey === selectedIcon;

    if (!IconComponent) return null;

    return (
      <TouchableOpacity
        key={iconKey}
        style={[styles.iconItem, isSelected && styles.iconItemSelected]}
        onPress={() => handleIconSelect(iconKey)}
      >
        <View style={[styles.iconContainer, isSelected && styles.iconContainerSelected]}>
          <IconComponent
            width={24}
            height={24}
            color={isSelected ? ADMIN_COLORS.PRIMARY : ADMIN_COLORS.TEXT_PRIMARY}
          />
        </View>
        <Text style={[styles.iconLabel, isSelected && styles.iconLabelSelected]}>
          {iconLabel}
        </Text>
      </TouchableOpacity>
    );
  };

  const iconEntries = Object.entries(resioIconOptionMappings);
  
  // Create rows of icons (2 per row)
  const iconRows = [];
  for (let i = 0; i < iconEntries.length; i += 2) {
    const row = iconEntries.slice(i, i + 2);
    iconRows.push(row);
  }

  return (
    <View style={styles.container}>
      <Text style={styles.label}>
        Icon <Text style={styles.required}>*</Text>
      </Text>
      
      <View>
        <TouchableOpacity
          ref={anchorRef}
          style={[styles.selector, error && styles.selectorError]}
          onPress={() => {
            if (Platform.OS === 'web' && anchorRef.current) {
              const element: any = anchorRef.current as any;
              const rect = element?.getBoundingClientRect?.();
              if (rect) {
                setAnchorRect({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
                setDropdownOpen(true);
                return;
              }
            }
            setShowModal(true);
          }}
        >
        <View style={styles.selectedIconContainer}>
          <SelectedIconComponent
            width={24}
            height={24}
            color={ADMIN_COLORS.TEXT_PRIMARY}
          />
        </View>
        <Text style={styles.selectedLabel}>{selectedIconLabel}</Text>
        <SVG_COMPONENTS.CHEVRON_DOWN
          width={20}
          height={20}
          color={ADMIN_COLORS.TEXT_SECONDARY}
        />
        </TouchableOpacity>
      </View>

      {error && <Text style={styles.errorText}>{error}</Text>}

      {/* Desktop web anchored dropdown */}
      <AnchoredDropdown
        isOpen={isDropdownOpen}
        anchor={anchorRect}
        width={anchorRect?.w ?? 300}
        maxHeight={360}
        onRequestClose={() => setDropdownOpen(false)}
      >
        <ScrollView style={{ maxHeight: 360 }} contentContainerStyle={{ paddingVertical: 8 }}>
          {iconEntries.map(([iconKey, iconLabel]) => {
            const IconComponent = COMMUNITY_RESOURCE_ICONS[iconKey as CommunityResourceIconKey];
            const isSelected = iconKey === selectedIcon;
            if (!IconComponent) return null;
            return (
              <TouchableOpacity
                key={`icon-${iconKey}`}
                style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: 12, backgroundColor: isSelected ? `${ADMIN_COLORS.PRIMARY}10` : 'transparent' }}
                onPress={() => handleIconSelect(iconKey)}
              >
                <View style={{ width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY, marginRight: 10 }}>
                  <IconComponent width={18} height={18} color={isSelected ? ADMIN_COLORS.PRIMARY : ADMIN_COLORS.TEXT_PRIMARY} />
                </View>
                <Text style={{ color: ADMIN_COLORS.TEXT_PRIMARY }}>{iconLabel}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </AnchoredDropdown>

      <Modal
        visible={showModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowModal(false)}
      >
        <View style={styles.modal}>
          <View style={styles.modalHeader}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => setShowModal(false)}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            
            <Text style={styles.modalTitle}>Select Icon</Text>
            
            <View style={styles.placeholder} />
          </View>
          
          <ScrollView style={styles.modalContent} contentContainerStyle={styles.iconGrid}>
            {iconRows.map((row, rowIndex) => (
              <View key={rowIndex} style={styles.row}>
                {row.map(([iconKey, iconLabel]) => renderIconItem(iconKey, iconLabel))}
              </View>
            ))}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
});

IconSelector.displayName = 'IconSelector';

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  required: {
    color: ADMIN_COLORS.ERROR,
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.SURFACE,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    padding: 12,
    minHeight: 48,
  },
  selectorError: {
    borderColor: ADMIN_COLORS.ERROR,
  },
  selectedIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  selectedLabel: {
    flex: 1,
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  errorText: {
    fontSize: 14,
    color: ADMIN_COLORS.ERROR,
    marginTop: 4,
  },
  modal: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  cancelButtonText: {
    fontSize: 16,
    color: ADMIN_COLORS.PRIMARY,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  placeholder: {
    width: 60, // Same width as cancel button for centering
  },
  modalContent: {
    flex: 1,
    padding: 16,
  },
  iconGrid: {
    paddingBottom: 20,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  iconItem: {
    flex: 1,
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 4,
    backgroundColor: ADMIN_COLORS.SURFACE,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  iconItemSelected: {
    borderColor: ADMIN_COLORS.PRIMARY,
    backgroundColor: `${ADMIN_COLORS.PRIMARY}10`,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  iconContainerSelected: {
    backgroundColor: `${ADMIN_COLORS.PRIMARY}20`,
  },
  iconLabel: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
  },
  iconLabelSelected: {
    color: ADMIN_COLORS.PRIMARY,
    fontWeight: '500',
  },
});